// src/pages/transport/TransportLog.js
// Transport Module — v1 — 27 May 2026
// Who logs: Store / Manager
// Approval: Direct to Accounts (no SM gate)
// Split: Equal auto-split, editable per stop
//
// v2 — Costing Card Part A: each stop now resolves its own project_id (via
// fetchProjectsForSite(), same auto-select-if-one pattern as elsewhere), and
// on submit a SEPARATE financial_transactions entry is created PER STOP
// (using that stop's allocated_cost + resolved project_id/site_id) instead
// of one single lump-sum entry for the whole trip. This is required because
// a single trip can service multiple sites/projects — a lump-sum entry could
// only ever be attributed to one project, silently mis-costing all the others.
// Store-destination and "other" (free-text) stops have no project to resolve
// (project attribution only applies to Client Site stops) — their portion of
// the cost still gets a financial_transactions entry, just without a
// project_id (an untagged transport cost, same as before this change for
// non-site stops).
//
// v3 — D-289 Phase 2 (07 Oct 2026): the list shows a trip as Paid when its
// own status is "paid" OR it was paid before D-289 (every stop's ledger row
// marked "paid" by the old Mark as Paid) — shared rule in
// src/utils/legacyPayments.js. Nothing is edited.

import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, addDoc, getDocs,
  query, where, orderBy, serverTimestamp
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { fetchProjectsForSite, autoSelectSiteIfOnlyOne } from "../../services/clientService";
import { legacyPaidTripIds } from "../../utils/legacyPayments";

// ── ENUMS ──────────────────────────────────────────────────────────────────
const VEHICLE_TYPE = {
  COMPANY: "company",
  HIRED:   "hired",
};

const COST_TYPE = {
  FUEL:         "fuel",
  HIRE_CHARGE:  "hire_charge",
  TOLL:         "toll",
  DRIVER_BATA:  "driver_bata",
  OTHER:        "other",
};

const COST_TYPE_LABELS = {
  fuel:         "Fuel Charge",
  hire_charge:  "Hire Charge",
  toll:         "Toll",
  driver_bata:  "Driver Bata",
  other:        "Other",
};

const STATUS_UI = {
  payment_pending: { label: "Payment Pending", bg: "#fef3c7", color: "#92400e" },
  paid:            { label: "Paid",            bg: "#d1fae5", color: "#065f46" },
};

const storage = getStorage();

// Company stores — hardcoded for now, future: fetch from stores collection
const STORES = [
  { id: "MAIN_STORE",  name: "Main Store" },
  { id: "TOOL_ROOM",   name: "Tool Room" },
  { id: "WAREHOUSE",   name: "Warehouse" },
];

export default function TransportLog() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const isSM      = ["sm", "owner", "manager", "store"].includes(user.role);

  // ── TABS ──────────────────────────────────────────────────────────────
  const [tab, setTab] = useState("list");

  // ── LIST STATE ─────────────────────────────────────────────────────────
  const [trips,       setTrips]       = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [legacyPaid,  setLegacyPaid]  = useState(new Set()); // D-289: trips paid by the old Mark as Paid

  // ── FORM STATE ─────────────────────────────────────────────────────────
  const [sites,        setSites]        = useState([]);
  const [services,     setServices]     = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileRef = useRef(null);

  const emptyForm = {
    trip_date:     "",
    vehicle_type:  VEHICLE_TYPE.COMPANY,
    vehicle_no:    "",
    driver_name:   "",
    cost_type:     "",
    total_cost:    "",
    from_location: "",
    service_id:    "",
    bill_url:      "",
    notes:         "",
  };
  const [form,  setForm]  = useState(emptyForm);
  const [stops, setStops] = useState([
    { site_id: "", site_name: "", allocated_cost: "", project_id: "", project_name: "" }
  ]);

  // Costing Card Part A — projects available per stop (keyed by stop index),
  // and a loading flag per stop so each stop's picker shows its own spinner
  // state independently while others aren't refetching.
  const [stopProjectsMap,        setStopProjectsMap]        = useState({});
  const [loadingStopProjectsMap, setLoadingStopProjectsMap] = useState({});

  // ── LOAD ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadTrips();
    loadSites();
    loadServices();
  }, []);

  const loadTrips = async () => {
    setListLoading(true);
    try {
      const q = query(
        collection(db, "transport_logs"),
        where("company_id", "==", companyId)
      );
      const snap = await getDocs(q);
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setTrips(list);

      // D-289 Phase 2 — trips paid before D-289 (all stop rows "paid").
      // Non-blocking: if this read fails, trips simply show their own status.
      try {
        const ftSnap = await getDocs(query(
          collection(db, "financial_transactions"),
          where("company_id", "==", companyId),
          where("reference_type", "==", "transport")
        ));
        setLegacyPaid(legacyPaidTripIds(ftSnap.docs.map(d => d.data())));
      } catch (legacyErr) {
        console.error("Legacy paid-trip check failed (non-blocking):", legacyErr);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, "sites"), where("company_id", "==", companyId))
      );
      setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const loadServices = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "service_tasks"),
        where("company_id", "==", companyId),
        where("status", "in", ["assigned", "in_progress", "partial"])
      ));
      setServices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  // Costing Card Part A — fetch projects linked to a stop's site, auto-select
  // if exactly one. Only meaningful for dest_type === "site" stops.
  const loadProjectsForStop = async (index, site_id) => {
    if (!site_id) {
      setStopProjectsMap(prev => ({ ...prev, [index]: [] }));
      return;
    }
    setLoadingStopProjectsMap(prev => ({ ...prev, [index]: true }));
    try {
      const projects = await fetchProjectsForSite(companyId, site_id);
      setStopProjectsMap(prev => ({ ...prev, [index]: projects }));
      const onlyProject = autoSelectSiteIfOnlyOne(projects);
      setStops(prevStops => {
        const updated = [...prevStops];
        if (updated[index]) {
          updated[index] = {
            ...updated[index],
            project_id:   onlyProject ? onlyProject.id : "",
            project_name: onlyProject ? (onlyProject.project_name || "") : "",
          };
        }
        return updated;
      });
    } catch (err) {
      console.error("Failed to load projects for stop:", err);
      setStopProjectsMap(prev => ({ ...prev, [index]: [] }));
    } finally {
      setLoadingStopProjectsMap(prev => ({ ...prev, [index]: false }));
    }
  };

  // ── STOPS — AUTO SPLIT ─────────────────────────────────────────────────
  const autoSplit = (totalCost, currentStops) => {
    const total = Number(totalCost || 0);
    const count = currentStops.length;
    if (total <= 0 || count === 0) return currentStops;

    const base      = Math.floor(total / count);
    const remainder = total - base * count;

    return currentStops.map((stop, i) => ({
      ...stop,
      allocated_cost: i === 0 ? base + remainder : base,
    }));
  };

  const addStop = () => {
    const newStops = [...stops, { site_id: "", site_name: "", allocated_cost: "", project_id: "", project_name: "" }];
    setStops(autoSplit(form.total_cost, newStops));
  };

  const removeStop = (index) => {
    if (stops.length === 1) return;
    const newStops = stops.filter((_, i) => i !== index);
    setStops(autoSplit(form.total_cost, newStops));
    setStopProjectsMap(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const updateStop = (index, field, value) => {
    const updated = [...stops];
    if (field === "site_id" && (!updated[index].dest_type || updated[index].dest_type === "site")) {
      const site = sites.find(s => s.id === value);
      updated[index] = {
        ...updated[index],
        site_id:   value,
        site_name: site?.site_name || "",
        project_id:   "", // reset until loadProjectsForStop resolves
        project_name: "",
      };
      setStops(updated);
      loadProjectsForStop(index, value);
      return;
    } else if (field === "dest_type") {
      // Reset site + project when type changes (project attribution only
      // applies to Client Site stops — Store/Other have nothing to resolve)
      updated[index] = {
        ...updated[index],
        dest_type:  value,
        site_id:    "",
        site_name:  "",
        project_id:   "",
        project_name: "",
      };
    } else {
      updated[index][field] = value;
    }
    setStops(updated);
  };

  const handleTotalCostChange = (val) => {
    setForm(f => ({ ...f, total_cost: val }));
    setStops(prev => autoSplit(val, prev));
  };

  // ── PHOTO UPLOAD ───────────────────────────────────────────────────────
  const handleBillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    try {
      const storageRef = ref(storage, `transport/${companyId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setForm(f => ({ ...f, bill_url: url }));
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setPhotoLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // ── VALIDATE ──────────────────────────────────────────────────────────
  const validate = () => {
    if (!form.trip_date)     { alert("Trip date required"); return false; }
    if (!form.driver_name.trim()) { alert("Driver name required"); return false; }
    if (!form.cost_type)     { alert("Cost type required"); return false; }
    if (!form.total_cost || Number(form.total_cost) <= 0) {
      alert("Valid total cost required"); return false;
    }
    if (stops.some(s => !s.site_name && !s.site_id)) {
      alert("Select or enter destination for all stops"); return false;
    }

    const splitTotal = stops.reduce((s, stop) => s + Number(stop.allocated_cost || 0), 0);
    const total      = Number(form.total_cost);
    if (Math.abs(splitTotal - total) > 1) {
      alert(`Split total ₹${splitTotal} doesn't match total cost ₹${total}. Please adjust.`);
      return false;
    }
    return true;
  };

  // ── SUBMIT ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const totalCost = Number(form.total_cost);

      const stopsForSave = stops.map(s => ({
        site_id:        s.site_id,
        site_name:      s.site_name,
        allocated_cost: Number(s.allocated_cost || 0),
        project_id:     s.project_id || "",
        project_name:   s.project_name || "",
      }));

      const tripRef = await addDoc(collection(db, "transport_logs"), {
        company_id:    companyId,
        logged_by:     user.uid || user.id || "",
        logged_by_name: user.name || "",
        trip_date:     form.trip_date,
        vehicle_type:  form.vehicle_type,
        vehicle_no:    form.vehicle_no.trim(),
        driver_name:   form.driver_name.trim(),
        cost_type:     form.cost_type,
        total_cost:    totalCost,
        from_location: form.from_location.trim(),
        service_id:    form.service_id || "",
        bill_url:      form.bill_url || "",
        notes:         form.notes.trim(),
        stops:         stopsForSave,
        status:        "payment_pending",  // Direct to accounts
        created_at:    serverTimestamp(),
        updated_at:    serverTimestamp(),
      });

      // Costing Card Part A — one financial_transactions entry PER STOP
      // (not one lump-sum for the whole trip), each tagged with that stop's
      // own allocated_cost + resolved project_id/site_id. reference_id now
      // correctly links back to the actual transport_logs doc (previously
      // left blank with a "will be updated if needed" comment that never
      // happened — fixed here since we now have tripRef.id in hand before
      // these writes happen).
      await Promise.all(stopsForSave.map((stop) =>
        addDoc(collection(db, "financial_transactions"), {
          company_id:        companyId,
          type:              "expense",
          category:          "transport",
          amount:            stop.allocated_cost,
          description:       `Transport: ${form.driver_name} — stop at ${stop.site_name || "destination"}`,
          payment_mode:      "",
          reference_type:    "transport",
          reference_id:      tripRef.id,
          project_id:        stop.project_id || "",
          project_name:      stop.project_name || "",
          site_id:           stop.site_id || "",
          site_name:         stop.site_name || "",
          requested_by:      user.uid || user.id || "",
          requested_by_name: user.name || "",
          service_id:        form.service_id || "",
          bill_url:          form.bill_url || "",
          approved_by:       user.uid || user.id || "",
          approved_by_name:  user.name || "",
          status:            "payment_pending",
          created_at:        serverTimestamp(),
          updated_at:        serverTimestamp(),
        })
      ));

      setForm(emptyForm);
      setStops([{ site_id: "", site_name: "", allocated_cost: "", project_id: "", project_name: "" }]);
      setStopProjectsMap({});
      setTab("list");
      loadTrips();
    } catch (err) {
      alert(err.message || "Failed to save trip");
    } finally {
      setSaving(false);
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────
  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen max-w-2xl mx-auto">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-slate-800">Transport</h1>
        {tab === "list" ? (
          <button onClick={() => setTab("create")}
            className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold">
            + Log Trip
          </button>
        ) : (
          <button onClick={() => {
            setTab("list");
            setForm(emptyForm);
            setStops([{ site_id: "", site_name: "", allocated_cost: "", project_id: "", project_name: "" }]);
            setStopProjectsMap({});
          }}
            className="text-sm text-slate-500 hover:text-slate-800">
            ← Back
          </button>
        )}
      </div>

      {/* ── LIST VIEW ── */}
      {tab === "list" && (
        <>
          {listLoading ? (
            <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
          ) : trips.length === 0 ? (
            <div className="text-center mt-16">
              <div className="text-4xl mb-3">🚚</div>
              <p className="text-slate-500 font-medium">No trips logged yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {trips.map(trip => {
                const statusUI = (trip.status === "paid" || legacyPaid.has(trip.id)) ? STATUS_UI.paid : STATUS_UI.payment_pending;
                return (
                  <div key={trip.id}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                    style={{ borderLeft: `4px solid ${statusUI.color}` }}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800">
                            🚚 {trip.driver_name}
                          </p>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: statusUI.bg, color: statusUI.color }}>
                            {statusUI.label}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {trip.trip_date} •
                          {trip.vehicle_type === "company" ? " Company Vehicle" : " Hired Vehicle"}
                          {trip.vehicle_no && ` • ${trip.vehicle_no}`}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {COST_TYPE_LABELS[trip.cost_type] || trip.cost_type}
                        </p>
                        {/* Stops */}
                        <div className="mt-2 space-y-1">
                          {(trip.stops || []).map((stop, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-slate-600">
                                📍 Stop {i + 1}: {stop.site_name}
                                {stop.project_name && ` · 🏗 ${stop.project_name}`}
                              </span>
                              <span className="text-slate-500 font-medium">
                                ₹{Number(stop.allocated_cost || 0).toLocaleString("en-IN")}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        <p className="text-xl font-black text-slate-800">
                          ₹{Number(trip.total_cost || 0).toLocaleString("en-IN")}
                        </p>
                        {trip.bill_url && (
                          <a href={trip.bill_url} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-500 underline">
                            📄 Bill
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── CREATE VIEW ── */}
      {tab === "create" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">

          {/* Date */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Trip Date *</label>
            <input type="date" value={form.trip_date}
              onChange={e => setForm(f => ({ ...f, trip_date: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Vehicle Type */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Vehicle Type *</label>
            <div className="flex gap-2">
              {[
                { val: VEHICLE_TYPE.COMPANY, label: "🏢 Company Vehicle" },
                { val: VEHICLE_TYPE.HIRED,   label: "🚗 Hired Vehicle" },
              ].map(opt => (
                <button key={opt.val} onClick={() => setForm(f => ({ ...f, vehicle_type: opt.val }))}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${
                    form.vehicle_type === opt.val
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-200 text-slate-500"
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle No */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Vehicle Number {form.vehicle_type === VEHICLE_TYPE.COMPANY ? "*" : "(optional)"}
            </label>
            <input placeholder="MH-01-AB-1234"
              value={form.vehicle_no}
              onChange={e => setForm(f => ({ ...f, vehicle_no: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Driver Name */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Driver Name *</label>
            <input placeholder="Driver / technician name"
              value={form.driver_name}
              onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Cost Type */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Cost Type *</label>
            <select value={form.cost_type}
              onChange={e => setForm(f => ({ ...f, cost_type: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
              <option value="">Select cost type</option>
              {Object.entries(COST_TYPE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          {/* Total Cost */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Total Cost (₹) *</label>
            <input type="number" placeholder="0" min="1"
              value={form.total_cost}
              onChange={e => handleTotalCostChange(e.target.value)}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* From Location */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">From Location</label>
            <input placeholder="Warehouse / Store / Office"
              value={form.from_location}
              onChange={e => setForm(f => ({ ...f, from_location: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Stops */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-500 uppercase">
                Delivery Stops *
              </label>
              <button onClick={addStop}
                className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-lg font-semibold hover:bg-slate-200">
                + Add Stop
              </button>
            </div>

            {/* Split info */}
            {form.total_cost && stops.length > 0 && (
              <p className="text-xs text-slate-400 mb-2">
                Auto-split: ₹{Number(form.total_cost)} ÷ {stops.length} stops.
                Edit amounts below if needed.
              </p>
            )}

            <div className="space-y-2">
              {stops.map((stop, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500">Stop {i + 1}</p>
                    {stops.length > 1 && (
                      <button onClick={() => removeStop(i)}
                        className="text-xs text-red-400 hover:text-red-600">
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
  {/* Destination Type */}
  <select
    value={stop.dest_type || "site"}
    onChange={e => updateStop(i, "dest_type", e.target.value)}
    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white">
    <option value="site">Client Site</option>
    <option value="store">Company Store</option>
    <option value="other">Other (free text)</option>
  </select>

  {/* Dynamic destination input */}
  {(stop.dest_type === "site" || !stop.dest_type) && (
    <select value={stop.site_id}
      onChange={e => updateStop(i, "site_id", e.target.value)}
      className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white">
      <option value="">Select site</option>
      {sites.map(s => (
        <option key={s.id} value={s.id}>{s.site_name}</option>
      ))}
    </select>
  )}

  {stop.dest_type === "store" && (
    <select value={stop.site_id}
      onChange={e => {
        const store = STORES.find(s => s.id === e.target.value);
        updateStop(i, "site_id", e.target.value);
        updateStop(i, "site_name", store?.name || "");
      }}
      className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white">
      <option value="">Select store</option>
      {STORES.map(s => (
        <option key={s.id} value={s.id}>{s.name}</option>
      ))}
    </select>
  )}

  {stop.dest_type === "other" && (
    <input placeholder="Enter destination name"
      value={stop.site_name}
      onChange={e => updateStop(i, "site_name", e.target.value)}
      className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
  )}

  {/* Costing Card Part A — per-stop project resolution. Only shown for
      Client Site stops with a site actually selected — Store/Other stops
      have no project to attribute to. */}
  {(stop.dest_type === "site" || !stop.dest_type) && stop.site_id && (
    <div className="mt-1">
      {loadingStopProjectsMap[i] ? (
        <p className="text-[11px] text-slate-400">Loading projects...</p>
      ) : (stopProjectsMap[i] || []).length === 0 ? (
        <p className="text-[11px] text-slate-400">No project linked to this site</p>
      ) : (stopProjectsMap[i] || []).length === 1 ? (
        <p className="text-[11px] text-emerald-600 font-semibold">
          ✓ {stop.project_name || stopProjectsMap[i][0].project_name}
        </p>
      ) : (
        <select value={stop.project_id}
          onChange={e => {
            const selected = (stopProjectsMap[i] || []).find(p => p.id === e.target.value);
            updateStop(i, "project_id", e.target.value);
            updateStop(i, "project_name", selected?.project_name || "");
          }}
          className="w-full border border-slate-200 rounded-xl p-2 text-xs outline-none bg-white">
          <option value="">No project link</option>
          {(stopProjectsMap[i] || []).map(p => (
            <option key={p.id} value={p.id}>{p.project_name}</option>
          ))}
        </select>
      )}
    </div>
  )}
</div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                      <input type="number" min="0"
                        value={stop.allocated_cost}
                        onChange={e => updateStop(i, "allocated_cost", e.target.value)}
                        className="w-full border border-slate-200 rounded-xl pl-7 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Split validation */}
            {form.total_cost && stops.length > 0 && (() => {
              const splitTotal = stops.reduce((s, stop) => s + Number(stop.allocated_cost || 0), 0);
              const total      = Number(form.total_cost);
              const diff       = total - splitTotal;
              return Math.abs(diff) > 1 ? (
                <p className="text-xs text-red-500 mt-1">
                  ⚠ Difference of ₹{Math.abs(diff)} remaining. Adjust stop amounts.
                </p>
              ) : (
                <p className="text-xs text-green-600 mt-1">✓ Split matches total</p>
              );
            })()}
          </div>

          {/* Link to Service */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Link to Service (optional)
            </label>
            <select value={form.service_id}
              onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
              <option value="">No service link</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>
                  {s.complaint || "Service"} — {s.site_name || ""}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Notes (optional)</label>
            <textarea rows={2} placeholder="Any additional info..."
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none resize-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Bill Upload */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Bill / Receipt (optional)
            </label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf"
              onChange={handleBillUpload} className="hidden" />
            <button onClick={() => fileRef.current?.click()} disabled={photoLoading}
              className="w-full py-2.5 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-500 transition">
              {photoLoading ? "Uploading..." : form.bill_url ? "✅ Bill uploaded — tap to change" : "📷 Upload Bill / Receipt"}
            </button>
            {form.bill_url && (
              <a href={form.bill_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-500 underline mt-1 block">
                View uploaded bill →
              </a>
            )}
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={saving}
            className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40">
            {saving ? "Saving..." : "Submit Trip"}
          </button>

        </div>
      )}
    </div>
  );
}
